/**
 * Création et correction d'un tiers.
 *
 * Le même formulaire sert aux deux : en création il part vide, en correction il
 * part du tiers existant et n'envoie que ce qui a changé. Deux formulaires
 * distincts finiraient par diverger sur la validation.
 */
import { useState, type FormEvent } from 'react';

import { ApiError, api, type Party, type PartyRoleKind } from '../api/client';
import { AvertissementDoublons } from './AvertissementDoublons';
import { Champ, ErreurFormulaire, Ligne, Panneau } from './Formulaire';
import { useDoublons } from './useDoublons';
import { doitInterrogerTiers, doublonsDeLErreur, type Doublon } from '../domaine/doublons';
import { ROLES_ORDONNES, ROLE_LIBELLE, rolesDeDepart } from '../domaine/roles';

interface Saisie {
  legalName: string;
  tradeName: string;
  legalIdentifier: string;
  vatNumber: string;
  email: string;
  phone: string;
  addressLine1: string;
  postalCode: string;
  city: string;
  notes: string;
  roles: PartyRoleKind[];
}

function depuis(tiers: Party | null, rolesInitiaux: readonly PartyRoleKind[]): Saisie {
  return {
    legalName: tiers?.legalName ?? '',
    tradeName: tiers?.tradeName ?? '',
    legalIdentifier: tiers?.legalIdentifier ?? '',
    vatNumber: tiers?.vatNumber ?? '',
    email: tiers?.email ?? '',
    phone: tiers?.phone ?? '',
    addressLine1: '',
    postalCode: tiers?.postalCode ?? '',
    city: tiers?.city ?? '',
    notes: '',
    roles: rolesDeDepart(tiers, rolesInitiaux),
  };
}

export function FormulaireTiers({
  tiers,
  rolesInitiaux = [],
  titreCreation = 'Nouveau tiers',
  onFermer,
  onEnregistre,
}: {
  /** `null` = création. */
  tiers: Party | null;
  /** Le titre du panneau en création : celui du bouton qui l'ouvre (« Nouveau client »). */
  titreCreation?: string | undefined;
  /** Les rôles cochés d'emblée à la CRÉATION (« Nouveau client » : Client) ; sans effet sur une correction. */
  rolesInitiaux?: readonly PartyRoleKind[] | undefined;
  onFermer: () => void;
  onEnregistre: (tiers: Party) => void;
}) {
  const [saisie, setSaisie] = useState<Saisie>(() => depuis(tiers, rolesInitiaux));
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);
  // Les fiches que le SERVEUR a rapprochées en refusant l'enregistrement (HTTP 409) : elles priment sur la
  // consultation faite pendant la saisie, et disparaissent dès que la raison sociale ou l'identifiant change.
  const [doublonsServeur, setDoublonsServeur] = useState<readonly Doublon[] | null>(null);

  const identiteCourante = { legalName: saisie.legalName, legalIdentifier: saisie.legalIdentifier };
  const doublonsEnSaisie = useDoublons(
    doitInterrogerTiers(
      identiteCourante,
      tiers ? { legalName: tiers.legalName, legalIdentifier: tiers.legalIdentifier ?? '' } : null,
    )
      ? () =>
          api
            .doublonsTiers({
              legalName: saisie.legalName,
              legalIdentifier: saisie.legalIdentifier,
              excludeId: tiers?.id,
            })
            .then((r) => r.doublons)
      : null,
    JSON.stringify(identiteCourante),
  );
  const doublons = doublonsServeur ?? doublonsEnSaisie;
  // « Client » quand le rôle est coché, « tiers » pour une marque, un fournisseur… : le mot de l'avertissement.
  const objetDoublon = saisie.roles.includes('CUSTOMER') ? 'client' : 'tiers';

  const modifier = <K extends keyof Saisie>(cle: K, valeur: Saisie[K]) => {
    if (cle === 'legalName' || cle === 'legalIdentifier') setDoublonsServeur(null);
    setSaisie((s) => ({ ...s, [cle]: valeur }));
  };

  const basculerRole = (role: PartyRoleKind) =>
    setSaisie((s) => ({
      ...s,
      roles: s.roles.includes(role) ? s.roles.filter((r) => r !== role) : [...s.roles, role],
    }));

  /** `confirmer` : l'utilisateur a vu l'avertissement de doublon et affirme que c'est un autre tiers. */
  const enregistrer = async (e: FormEvent | undefined, confirmer = false) => {
    e?.preventDefault();
    if (!saisie.legalName.trim()) {
      setErreur(new Error('La raison sociale est obligatoire.'));
      return;
    }

    setEnCours(true);
    setErreur(null);
    try {
      // Les champs vides sont envoyés en `null` : c'est ainsi qu'on EFFACE une
      // valeur. Les omettre les laisserait inchangés.
      const corps = {
        legalName: saisie.legalName.trim(),
        tradeName: vide(saisie.tradeName),
        legalIdentifier: vide(saisie.legalIdentifier),
        vatNumber: vide(saisie.vatNumber),
        email: vide(saisie.email),
        phone: vide(saisie.phone),
        addressLine1: vide(saisie.addressLine1),
        postalCode: vide(saisie.postalCode),
        city: vide(saisie.city),
        notes: vide(saisie.notes),
        roles: saisie.roles,
        ...(confirmer ? { confirmerDoublon: true } : {}),
      };

      const resultat = tiers
        ? await api.updateParty(tiers.id, corps)
        : await api.createParty(sansNull(corps) as Record<string, unknown> & { legalName: string });

      onEnregistre(resultat);
      onFermer();
    } catch (e) {
      // Un doublon probable n'est pas une panne : on montre les fiches existantes et on attend la décision.
      const rapprochees = doublonsDeLErreur(e);
      if (rapprochees) {
        setDoublonsServeur(rapprochees);
        setErreur(null);
      } else {
        setErreur(e instanceof ApiError ? e : new Error('Enregistrement impossible.'));
      }
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={tiers ? 'Corriger le tiers' : titreCreation}
      sousTitre={
        tiers
          ? 'Les rôles se cumulent : cochez tous ceux qui s’appliquent.'
          : 'Client, fournisseur, sous-traitant, marque… les rôles se cumulent.'
      }
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={(e) => void enregistrer(e)} disabled={enCours}>
            {enCours ? 'Enregistrement…' : tiers ? 'Enregistrer' : 'Créer le tiers'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void enregistrer(e)}>
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        <AvertissementDoublons
          objet={objetDoublon}
          doublons={doublons}
          correction={Boolean(tiers)}
          enCours={enCours}
          defiler={doublonsServeur !== null}
          onConfirmer={() => void enregistrer(undefined, true)}
        />

        <Champ libelle="Raison sociale" requis aide="Le nom qui figure sur les documents officiels.">
          <input
            type="text"
            autoFocus
            value={saisie.legalName}
            onChange={(e) => modifier('legalName', e.target.value)}
          />
        </Champ>

        <Champ libelle="Nom commercial" aide="Le nom d’usage, s’il diffère.">
          <input
            type="text"
            value={saisie.tradeName}
            onChange={(e) => modifier('tradeName', e.target.value)}
          />
        </Champ>

        <Ligne>
          <Champ libelle="SIRET / identifiant légal">
            <input
              type="text"
              value={saisie.legalIdentifier}
              onChange={(e) => modifier('legalIdentifier', e.target.value)}
            />
          </Champ>
          <Champ libelle="N° de TVA">
            <input
              type="text"
              value={saisie.vatNumber}
              onChange={(e) => modifier('vatNumber', e.target.value)}
            />
          </Champ>
        </Ligne>

        <Ligne>
          <Champ libelle="Adresse électronique">
            <input
              type="text"
              value={saisie.email}
              onChange={(e) => modifier('email', e.target.value)}
            />
          </Champ>
          <Champ libelle="Téléphone">
            <input
              type="text"
              value={saisie.phone}
              onChange={(e) => modifier('phone', e.target.value)}
            />
          </Champ>
        </Ligne>

        <Champ libelle="Adresse">
          <input
            type="text"
            value={saisie.addressLine1}
            onChange={(e) => modifier('addressLine1', e.target.value)}
          />
        </Champ>

        <Ligne>
          <Champ libelle="Code postal">
            <input
              type="text"
              value={saisie.postalCode}
              onChange={(e) => modifier('postalCode', e.target.value)}
            />
          </Champ>
          <Champ libelle="Ville">
            <input
              type="text"
              value={saisie.city}
              onChange={(e) => modifier('city', e.target.value)}
            />
          </Champ>
        </Ligne>

        <Champ libelle="Rôles" aide="Un tiers peut en porter plusieurs à la fois.">
          <div className="cases">
            {ROLES_ORDONNES.map((role) => (
              <label key={role} className="case">
                <input
                  type="checkbox"
                  checked={saisie.roles.includes(role)}
                  onChange={() => basculerRole(role)}
                />
                {ROLE_LIBELLE[role]}
              </label>
            ))}
          </div>
        </Champ>

        <Champ libelle="Remarques">
          <textarea
            rows={3}
            value={saisie.notes}
            onChange={(e) => modifier('notes', e.target.value)}
          />
        </Champ>
      </form>
    </Panneau>
  );
}

function vide(v: string): string | null {
  return v.trim() === '' ? null : v.trim();
}

/** À la création, les champs nuls sont simplement omis. */
function sansNull(corps: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(corps).filter(([, v]) => v !== null));
}
